import React from "react";
import { SOON_TOOLTIP } from "../../atoms/SoonBadge/SoonBadge";
import { ChevronRight, ChevronLeft, Plus } from "react-feather";
import {
  BottomSection,
  CreateButton,
  ExpandToggle,
  IconFrame,
  ItemLabel,
  RailAnchor,
  DisabledNavigationItem,
  NavigationItem,
  NavigationLink,
  NavigationList,
  NavigationSection,
  Rail,
} from "./ContextNavigationRail.styles";
import { useContextNavigationRailInternalState } from "./useContextNavigationRailInternalState";
import type { ContextNavigationRailProps } from "./ContextNavigationRail.types";

/**
 * Icon-only primary navigation used beside contextual KG and connector panels.
 * Route matching stays in item configuration; this component only renders it.
 */
export const ContextNavigationRail: React.FC<
  ContextNavigationRailProps
> = ({ items, utilityItems = [], pathname, profile, onCreate, isExpandable = false }) => {
  const rail = useContextNavigationRailInternalState(isExpandable);
  const renderItem = ({
    id,
    to,
    label,
    icon: Icon,
    isSelected,
    soon,
  }: (typeof items)[number]) => {
    if (soon) {
      // Not built yet: greyed out, named as coming soon, never a link.
      return (
        <NavigationItem key={id}>
          <DisabledNavigationItem
            aria-disabled="true"
            aria-label={`${label}, coming soon`}
            title={SOON_TOOLTIP}
            $wide={rail.isExpanded}
          >
            <IconFrame aria-hidden="true">
              <Icon size={16} />
            </IconFrame>
            {rail.isExpanded && <ItemLabel>{label}</ItemLabel>}
          </DisabledNavigationItem>
        </NavigationItem>
      );
    }
    const isActive = isSelected(pathname);

    return (
      <NavigationItem key={id}>
        <NavigationLink
          to={to}
          $isActive={isActive}
          aria-label={label}
          aria-current={isActive ? "page" : undefined}
          title={label}
          $wide={rail.isExpanded}
        >
          <IconFrame aria-hidden="true">
            <Icon size={16} />
          </IconFrame>
          {rail.isExpanded && <ItemLabel>{label}</ItemLabel>}
        </NavigationLink>
      </NavigationItem>
    );
  };

  const rendered = (
    <Rail
      aria-label="Primary navigation"
      $expandable={isExpandable}
      $expanded={rail.isExpanded}
      onMouseEnter={rail.onMouseEnter}
      onMouseLeave={rail.onMouseLeave}
      onFocus={rail.onFocus}
      onBlur={rail.onBlur}
      onKeyDown={rail.onKeyDown}
    >
      <NavigationSection>
        {isExpandable && (
          <ExpandToggle
            type="button"
            onClick={rail.toggle}
            aria-expanded={rail.isExpanded}
            aria-label={rail.isPinned ? "Collapse navigation" : "Expand navigation"}
            title={rail.isPinned ? "Collapse navigation" : "Expand navigation"}
          >
            {rail.isPinned ? <ChevronLeft size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
          </ExpandToggle>
        )}
        <NavigationList>{items.map(renderItem)}</NavigationList>
      </NavigationSection>

      <BottomSection>
        <NavigationList>
          {utilityItems.map(renderItem)}
          <NavigationItem>{profile}</NavigationItem>
        </NavigationList>
        <CreateButton
          type="button"
          onClick={onCreate}
          aria-label="Create data story"
          title="Create data story"
        >
          <Plus aria-hidden="true" />
        </CreateButton>
      </BottomSection>
    </Rail>
  );

  return isExpandable ? <RailAnchor>{rendered}</RailAnchor> : rendered;
};

export default ContextNavigationRail;
